import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Github, ExternalLink, FileText, ChevronDown, ChevronUp } from 'lucide-react';
// Props: researchProjects

const Research = ({ researchProjects }) => {
  const [expandedProject, setExpandedProject] = useState(null);

  if (!researchProjects) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Published' },
      manuscript: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Manuscript in Prep' },
      ongoing: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Ongoing' }
    };
    return statusConfig[status] || statusConfig.ongoing;
  };

  const ProjectCard = ({ project }) => {
    const isExpanded = expandedProject === project.id;
    const statusBadge = getStatusBadge(project.status);

    return (
      <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-lg">
        <CardHeader>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${statusBadge.color} border`}>
                  {statusBadge.label}
                </Badge>
                <span className="text-sm text-gray-500">{project.duration}</span>
              </div>
              <CardTitle className="text-2xl mb-2">{project.title}</CardTitle>
              <CardDescription className="text-base">
                {project.shortDescription}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Technologies */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, index) => (
                <Badge key={index} variant="secondary" className="bg-slate-100 text-gray-700">
                  {tech}
                </Badge>
              ))}
            </div>
          </div>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="space-y-4 mb-4 animate-in slide-in-from-top">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Full Description</h4>
                <p className="text-gray-700 leading-relaxed">{project.fullDescription}</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Key Highlights</h4>
                <ul className="space-y-2">
                  {project.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-blue-900 mt-1">•</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {project.manuscript && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    <strong>Status:</strong> {project.manuscript}
                  </p>
                </div>
              )}

              {project.publication && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-900">
                    <strong>Published in:</strong> {project.publication}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {project.status !== "published" && !project.github && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-gray-600 mb-4">
              Repository will be released upon publication.
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setExpandedProject(isExpanded ? null : project.id)}
              variant="outline"
              size="sm"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Read More
                </>
              )}
            </Button>
            
            {project.github && (
              <Button
                onClick={() => window.open(project.github, '_blank')}
                variant="outline"
                size="sm"
              >
                <Github className="w-4 h-4 mr-2" />
                GitHub
              </Button>
            )}
            
            {project.doi && (
              <Button
                onClick={() => window.open(`https://doi.org/${project.doi}`, '_blank')}
                variant="outline"
                size="sm"
                className="bg-green-50 hover:bg-green-100 border-green-200"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Publication
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const publishedProjects = researchProjects.filter(p => p.status === 'published');
  const ongoingProjects = researchProjects.filter(p => p.status === 'ongoing');
  const manuscriptProjects = researchProjects.filter(p => p.status === 'manuscript');

  return (
    <section id="research" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Research Projects</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Focused on efficient AI architectures, computer vision, and safety-critical systems
          </p>
          <div className="w-20 h-1 bg-blue-900 mx-auto mt-6"></div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-4 mb-12">
            <TabsTrigger value="all">All ({researchProjects.length})</TabsTrigger>
            <TabsTrigger value="published">Published ({publishedProjects.length})</TabsTrigger>
            <TabsTrigger value="ongoing">Ongoing ({ongoingProjects.length})</TabsTrigger>
            <TabsTrigger value="manuscript">Manuscripts ({manuscriptProjects.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-8">
            {researchProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </TabsContent>

          <TabsContent value="published" className="space-y-8">
            {publishedProjects.length > 0 ? (
              publishedProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <p className="text-center text-gray-500">No published projects yet.</p>
            )}
          </TabsContent>

          <TabsContent value="ongoing" className="space-y-8">
            {ongoingProjects.length > 0 ? (
              ongoingProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <p className="text-center text-gray-500">No ongoing projects.</p>
            )}
          </TabsContent>

          <TabsContent value="manuscript" className="space-y-8">
            {manuscriptProjects.length > 0 ? (
              manuscriptProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <p className="text-center text-gray-500">No manuscripts in preparation.</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Research;
